/**
 * Data layer for `creator-drilldown`. Thin typed fetch wrappers over /api/v1/creator-drilldown.
 * BE exposes POST /events, GET /creator/:id/summary, GET /creator/:id/timeline.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type EventType = "click" | "conversion" | "payout";
export type BucketKind = "day" | "week";

export interface EventRow {
	id: string;
	tenant_id: string;
	influencer_id: string;
	campaign_id: string;
	event_type: EventType;
	amount_cents: number;
	ts: number;
}

export interface LogEventBody {
	influencerId: string;
	campaignId: string;
	eventType: EventType;
	amountCents?: number;
	ts?: number | null;
}

export interface SummaryResponse {
	influencer_id: string;
	from: number;
	to: number;
	clicks: number;
	conversions: number;
	cr_bps: number;
	revenue_cents: number;
	earned_cents: number;
}

export interface TimelinePoint {
	bucket_start: number;
	clicks: number;
	conversions: number;
	revenue_cents: number;
	earned_cents: number;
}

export interface TimelineResponse {
	influencer_id: string;
	from: number;
	to: number;
	bucket: BucketKind;
	series: TimelinePoint[];
	count: number;
}

const BASE = "/api/v1/creator-drilldown";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function fetchSummary(input: {
	influencerId: string;
	from: number;
	to: number;
}): Promise<SummaryResponse> {
	const q = new URLSearchParams({
		from: String(input.from),
		to: String(input.to),
	});
	const res = await fetch(
		`${BASE}/creator/${encodeURIComponent(input.influencerId)}/summary?${q.toString()}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<SummaryResponse>(res);
}

export async function fetchTimeline(input: {
	influencerId: string;
	from: number;
	to: number;
	bucket?: BucketKind;
}): Promise<TimelineResponse> {
	const q = new URLSearchParams({
		from: String(input.from),
		to: String(input.to),
	});
	if (input.bucket) q.set("bucket", input.bucket);
	const res = await fetch(
		`${BASE}/creator/${encodeURIComponent(input.influencerId)}/timeline?${q.toString()}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<TimelineResponse>(res);
}

export async function logEvent(body: LogEventBody): Promise<{ event: EventRow }> {
	const res = await fetch(`${BASE}/events`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ event: EventRow }>(res);
}

export const creatorDrilldownQueryKeys = {
	all: ["creator-drilldown"] as const,
	summary: (influencerId: string, from: number, to: number) =>
		[...creatorDrilldownQueryKeys.all, "summary", influencerId, from, to] as const,
	timeline: (
		influencerId: string,
		from: number,
		to: number,
		bucket: BucketKind,
	) =>
		[
			...creatorDrilldownQueryKeys.all,
			"timeline",
			influencerId,
			from,
			to,
			bucket,
		] as const,
};
